/**
 * Formats a price in kobo to Nigerian Naira display format
 * @param kobo - Price in kobo (e.g., 1000 = ₦10.00)
 * @returns Formatted price string (e.g., "₦10.00" or "₦34,000.00")
 */
export const formatPrice = (kobo: number): string => {
  const naira = kobo / 100
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(naira)
}

/**
 * Formats a price input value with NGN symbol and proper decimal placement
 * Handles user input and formats it in real-time
 * @param value - Input value (can be string or number)
 * @returns Formatted string for display in input field
 */
export const formatPriceInput = (value: string | number): string => {
  // Remove all non-digit characters
  const digitsOnly = value.toString().replace(/\D/g, '')

  if (!digitsOnly) {
    return ''
  }

  // Convert to number and divide by 100 to get naira from kobo
  const kobo = parseInt(digitsOnly, 10)
  const naira = kobo / 100

  // Format with commas and 2 decimal places
  return naira.toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Parses a formatted price string back to kobo for backend
 * @param formatted - Formatted price string (e.g., "10.00" or "34,000.00")
 * @returns Price in kobo (e.g., 1000 or 3400000)
 */
export const parsePriceToKobo = (formatted: string | number): number => {
  if (typeof formatted === "number") {
    return formatted;
  }

  // Remove all non-digit and non-decimal characters
  const cleaned = formatted.replace(/[^\d.]/g, "");

  if (!cleaned) {
    return 0;
  }

  // Parse as float and multiply by 100 to get kobo
  const naira = parseFloat(cleaned);

  return Math.round(naira * 100)
}

/**
 * Formats kobo to naira for input field initial value
 * @param kobo - Price in kobo
 * @returns Formatted string for input display
 */
export const koboToInputValue = (kobo: number | string): string => {

  const koboNum = typeof kobo === 'string' ? parseInt(kobo, 10) : kobo

  if (isNaN(koboNum)) {
    return ''
  }

  const naira = koboNum / 100

  return naira.toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}
